import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFocusTrap } from '_components';
import * as api from './backupsApi';
import {
    DeliveryBadge, Empty, ErrorBanner, Loading, commitLabel, deliveryHint,
    formatTime, shortSha, timeAgo,
} from './backupsUi';

export { BackupRouter };

const PAGE = 50;

function BackupRouter({ routerId, isAdmin, onBack }) {
    const [generations, setGenerations] = useState([]);
    const [generation, setGeneration] = useState(null);
    const [history, setHistory] = useState({ commits: [], total: 0, delivery: 'none', state: null });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [selectedSha, setSelectedSha] = useState(null);
    const [tab, setTab] = useState('changes');
    const [confirmNew, setConfirmNew] = useState(false);
    const [starting, setStarting] = useState(false);

    const loadGenerations = useCallback(async () => {
        const data = await api.fetchGenerations(routerId);
        const gens = data.generations || [];
        setGenerations(gens);
        return gens;
    }, [routerId]);

    const loadHistory = useCallback(async (gen) => {
        const data = await api.fetchCommits(routerId, { generation: gen, limit: PAGE });
        setHistory({
            commits: data.commits || [], total: data.total || 0,
            delivery: data.delivery, state: data.state,
        });
        setSelectedSha((prev) => {
            const commits = data.commits || [];
            return commits.some((c) => c.sha === prev) ? prev : commits[0]?.sha || null;
        });
    }, [routerId]);

    // Initial load: generations, then the active one's history.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const gens = await loadGenerations();
                if (cancelled) return;
                const active = gens.find((g) => !g.archived_at) || gens[0];
                if (!active) {
                    setHistory({ commits: [], total: 0, delivery: 'none', state: null });
                    return;
                }
                setGeneration(active.generation);
                await loadHistory(active.generation);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [loadGenerations, loadHistory]);

    const switchGeneration = async (gen) => {
        setGeneration(gen);
        setLoading(true);
        setError(null);
        try {
            await loadHistory(gen);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            const data = await api.fetchCommits(routerId, { generation, skip: history.commits.length, limit: PAGE });
            setHistory((h) => ({ ...h, commits: [...h.commits, ...(data.commits || [])] }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingMore(false);
        }
    };

    const startNewGeneration = async () => {
        setStarting(true);
        try {
            const res = await api.startGeneration(routerId);
            setConfirmNew(false);
            await loadGenerations();
            await switchGeneration(res.generation);
        } catch (err) {
            setError(err.message);
        } finally {
            setStarting(false);
        }
    };

    const current = generations.find((g) => g.generation === generation);
    const archived = Boolean(current?.archived_at);
    const selected = history.commits.find((c) => c.sha === selectedSha) || null;
    const state = history.state;

    const tabs = [
        { id: 'changes', label: 'Changes', icon: 'difference' },
        { id: 'files', label: 'Files', icon: 'folder_open' },
        { id: 'details', label: 'Details', icon: 'info' },
        ...(isAdmin ? [{ id: 'access', label: 'Access log', icon: 'policy' }] : []),
    ];

    return (
        <div className="bk-page">
            <div className="bk-hero">
                <div>
                    <button type="button" className="bk-back" onClick={onBack}>
                        <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
                        All routers
                    </button>
                    <div className="bk-breadcrumb">
                        <span className="bk-breadcrumb-dot" />
                        <span className="bk-breadcrumb-text">CONFIG_BACKUPS / {routerId}</span>
                    </div>
                    <h1 className="bk-title bk-mono-title">{routerId}</h1>
                </div>
                <div className="bk-hero-actions">
                    {!archived && <DeliveryBadge delivery={history.delivery} />}
                    {archived && (
                        <span className="bk-badge bk-badge-idle">
                            <span className="material-symbols-outlined" aria-hidden="true">inventory_2</span>
                            Archived, read-only
                        </span>
                    )}
                    {generations.length > 1 && (
                        <label className="bk-select-label">
                            <span>Repository</span>
                            <select
                                className="bk-select"
                                value={generation ?? ''}
                                onChange={(e) => switchGeneration(Number(e.target.value))}
                            >
                                {generations.map((g) => (
                                    <option key={g.generation} value={g.generation}>
                                        Generation {g.generation}
                                        {g.archived_at ? ` (archived ${formatTime(g.archived_at)})` : ' (current)'}
                                        {` — ${g.commit_count} commits`}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
            </div>

            <ErrorBanner msg={error} onClose={() => setError(null)} />

            {!archived && history.delivery === 'conflict' && (
                <div className="bk-conflict" role="alert">
                    <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                    <div className="bk-conflict-text">
                        <strong>This router sends a history that does not belong to this repository.</strong>
                        <span>
                            That happens when a box is replaced or restored and keeps the same router id. Its backups
                            cannot be stored here, and it will keep resending. Start a new repository for it; the
                            current one is kept, read-only, as the previous box&apos;s history.
                        </span>
                    </div>
                    {isAdmin ? (
                        <button type="button" className="bk-btn bk-btn-danger" onClick={() => setConfirmNew(true)}>
                            Start new repository
                        </button>
                    ) : (
                        <span className="bk-conflict-note">Ask an administrator to start a new repository.</span>
                    )}
                </div>
            )}

            {!archived && ['failed', 'refused', 'pending'].includes(history.delivery) && (
                <div className={`bk-notice bk-notice-${history.delivery === 'failed' ? 'error' : 'warn'}`}>
                    <span className="material-symbols-outlined" aria-hidden="true">info</span>
                    <span>
                        {deliveryHint(history.delivery)}
                        {history.delivery === 'failed' && state?.last_error && (
                            <span className="bk-mono bk-notice-detail"> {state.last_error}</span>
                        )}
                        {history.delivery === 'refused' && state?.last_ack_status ? (
                            <span className="bk-mono bk-notice-detail"> HTTP {state.last_ack_status} {state.last_ack_body}</span>
                        ) : null}
                    </span>
                </div>
            )}

            {loading ? (
                <div className="bk-card"><Loading label="Loading history" /></div>
            ) : history.commits.length === 0 ? (
                <div className="bk-card">
                    <Empty icon="cloud_off" title={archived ? 'This repository is empty' : 'No backups received yet'}>
                        {archived ? null : (
                            <>
                                On the router: <span className="bk-mono">configure system backup enable true</span>, then{' '}
                                <span className="bk-mono">commit</span>. The first backup arrives with the next configuration
                                change, or at once with <span className="bk-mono">system backup push</span>.
                            </>
                        )}
                    </Empty>
                </div>
            ) : (
                <div className="bk-layout">
                    <Timeline
                        commits={history.commits}
                        total={history.total}
                        selectedSha={selectedSha}
                        onSelect={setSelectedSha}
                        onLoadMore={loadMore}
                        loadingMore={loadingMore}
                        state={state}
                    />
                    <section className="bk-card bk-detail" aria-label="Selected backup">
                        {selected && (
                            <>
                                <div className="bk-detail-header">
                                    <div className="bk-detail-heading">
                                        <div className="bk-detail-title">
                                            <span className="bk-mono">{commitLabel(selected)}</span>
                                            <span className="bk-detail-sub">{formatTime(selected.committed_at)}</span>
                                        </div>
                                        <div className="bk-detail-sub bk-mono">{selected.sha}</div>
                                    </div>
                                    <DownloadButton routerId={routerId} sha={selected.sha} generation={generation} onError={setError} />
                                </div>
                                <div className="bk-tab-bar" role="tablist" aria-label="Backup views">
                                    {tabs.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={tab === t.id}
                                            className={`bk-tab-btn${tab === t.id ? ' active' : ''}`}
                                            onClick={() => setTab(t.id)}
                                        >
                                            <span className="material-symbols-outlined" aria-hidden="true">{t.icon}</span>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="bk-tab-body" role="tabpanel">
                                    {tab === 'changes' && (
                                        <ChangesTab
                                            routerId={routerId}
                                            generation={generation}
                                            commit={selected}
                                            commits={history.commits}
                                        />
                                    )}
                                    {tab === 'files' && (
                                        <FilesTab routerId={routerId} generation={generation} sha={selected.sha} />
                                    )}
                                    {tab === 'details' && <DetailsTab commit={selected} state={state} generation={current} />}
                                    {tab === 'access' && isAdmin && <AccessTab routerId={routerId} />}
                                </div>
                            </>
                        )}
                    </section>
                </div>
            )}

            {confirmNew && (
                <ConfirmNewRepository
                    routerId={routerId}
                    busy={starting}
                    onCancel={() => setConfirmNew(false)}
                    onConfirm={startNewGeneration}
                />
            )}
        </div>
    );
}

// ── Timeline ─────────────────────────────────────────────────────────
function Timeline({ commits, total, selectedSha, onSelect, onLoadMore, loadingMore, state }) {
    return (
        <nav className="bk-card bk-timeline" aria-label="Backup history">
            <div className="bk-card-header">
                <h2 className="bk-card-title">History</h2>
                <span className="bk-card-meta">{total} commits</span>
            </div>
            <ol className="bk-timeline-list">
                {commits.map((c, i) => {
                    const isHead = state?.head === c.sha;
                    return (
                        <li key={c.sha}>
                            <button
                                type="button"
                                className={`bk-timeline-item${c.sha === selectedSha ? ' active' : ''}`}
                                onClick={() => onSelect(c.sha)}
                                aria-current={c.sha === selectedSha ? 'true' : undefined}
                            >
                                <span className={`bk-timeline-dot${i === 0 ? ' head' : ''}`} aria-hidden="true" />
                                <span className="bk-timeline-main">
                                    <span className="bk-timeline-row">
                                        <span className="bk-mono bk-timeline-label">{commitLabel(c)}</span>
                                        <span className="bk-timeline-time" title={formatTime(c.committed_at)}>
                                            {timeAgo(c.committed_at)}
                                        </span>
                                    </span>
                                    {c.body && <span className="bk-timeline-comment">{c.body}</span>}
                                    {isHead && i !== 0 && <span className="bk-timeline-sub">server head</span>}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ol>
            {commits.length < total && (
                <button type="button" className="bk-btn bk-btn-block" onClick={onLoadMore} disabled={loadingMore}>
                    {loadingMore ? 'Loading…' : `Load older (${total - commits.length} more)`}
                </button>
            )}
        </nav>
    );
}

// ── Download ─────────────────────────────────────────────────────────
function DownloadButton({ routerId, sha, generation, onError }) {
    const [busy, setBusy] = useState(false);
    const download = async () => {
        setBusy(true);
        try {
            await api.downloadArchive(routerId, sha, generation);
        } catch (err) {
            onError(err.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            type="button"
            className="bk-btn"
            onClick={download}
            disabled={busy}
            title="The archive netc import restores from"
        >
            <span className="material-symbols-outlined" aria-hidden="true">download</span>
            {busy ? 'Preparing…' : 'Download .tar.gz'}
        </button>
    );
}

// ── Changes ──────────────────────────────────────────────────────────
function ChangesTab({ routerId, generation, commit, commits }) {
    const [from, setFrom] = useState('');
    const [path, setPath] = useState('');
    const [diff, setDiff] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // A new selection compares with its parent again.
    useEffect(() => { setFrom(''); setPath(''); }, [commit.sha]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.fetchDiff(routerId, { to: commit.sha, from: from || undefined, path: path || undefined, generation })
            .then((d) => { if (!cancelled) setDiff(d); })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [routerId, generation, commit.sha, from, path]);

    const others = commits.filter((c) => c.sha !== commit.sha);

    return (
        <div>
            <div className="bk-toolbar">
                <label className="bk-select-label">
                    <span>Compare with</span>
                    <select className="bk-select" value={from} onChange={(e) => setFrom(e.target.value)}>
                        <option value="">{commit.parent ? 'Previous commit' : 'Nothing (first backup)'}</option>
                        {others.map((c) => (
                            <option key={c.sha} value={c.sha}>
                                {commitLabel(c)} · {formatTime(c.committed_at)}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="bk-select-label">
                    <span>File</span>
                    <select className="bk-select" value={path} onChange={(e) => setPath(e.target.value)}>
                        <option value="">All files</option>
                        <option value="startup-config">startup-config</option>
                    </select>
                </label>
            </div>
            <ErrorBanner msg={error} />
            {loading ? <Loading label="Loading changes" /> : diff && <DiffView diff={diff} />}
        </div>
    );
}

function classifyDiffLine(line) {
    if (line.startsWith('diff --git')) return 'file';
    if (line.startsWith('+++') || line.startsWith('---')) return 'meta';
    if (/^(index |new file mode|deleted file mode|old mode|new mode|similarity|rename |Binary files)/.test(line)) return 'meta';
    if (line.startsWith('@@')) return 'hunk';
    if (line.startsWith('+')) return 'add';
    if (line.startsWith('-')) return 'del';
    if (line.startsWith('\\')) return 'meta';
    return 'ctx';
}

function DiffView({ diff }) {
    const files = useMemo(() => {
        const out = [];
        let cur = null;
        diff.patch.split('\n').forEach((line) => {
            if (line.startsWith('diff --git')) {
                const m = line.match(/^diff --git a\/(.*) b\/(.*)$/);
                cur = { name: m ? m[2] : line, lines: [], add: 0, del: 0 };
                out.push(cur);
                return;
            }
            if (!cur) return;
            const kind = classifyDiffLine(line);
            if (kind === 'add' && !line.startsWith('+++')) cur.add += 1;
            if (kind === 'del' && !line.startsWith('---')) cur.del += 1;
            cur.lines.push({ text: line, kind });
        });
        return out;
    }, [diff.patch]);

    if (files.length === 0) {
        return <Empty icon="check" title="No differences" />;
    }
    return (
        <div className="bk-diff">
            {diff.truncated && (
                <div className="bk-notice bk-notice-warn">
                    <span className="material-symbols-outlined" aria-hidden="true">content_cut</span>
                    The diff is too large and was cut. Download the archive to see everything.
                </div>
            )}
            <div className="bk-diff-summary">
                {files.length} {files.length === 1 ? 'file' : 'files'} changed
            </div>
            {files.map((f) => (
                <details key={f.name} className="bk-diff-file" open={files.length <= 8 || f.name === 'startup-config'}>
                    <summary className="bk-diff-file-head">
                        <span className="bk-mono">{f.name}</span>
                        <span className="bk-diff-stat">
                            <span className="add">+{f.add}</span> <span className="del">−{f.del}</span>
                        </span>
                    </summary>
                    <pre className="bk-code bk-diff-body">
                        {f.lines.filter((l) => l.kind !== 'meta').map((l, i) => (
                            <span key={i} className={`bk-diff-line ${l.kind}`}>{l.text || ' '}{'\n'}</span>
                        ))}
                    </pre>
                </details>
            ))}
        </div>
    );
}

// ── Files ────────────────────────────────────────────────────────────
function FilesTab({ routerId, generation, sha }) {
    const [files, setFiles] = useState(null);
    const [path, setPath] = useState('startup-config');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        let cancelled = false;
        setFiles(null);
        api.fetchTree(routerId, sha, generation)
            .then((d) => {
                if (cancelled) return;
                const list = d.files || [];
                setFiles(list);
                setPath((p) => (list.some((f) => f.path === p) ? p : list[0]?.path || ''));
            })
            .catch((err) => { if (!cancelled) setError(err.message); });
        return () => { cancelled = true; };
    }, [routerId, generation, sha]);

    useEffect(() => {
        if (!path) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.fetchFile(routerId, sha, path, generation)
            .then((f) => { if (!cancelled) setFile(f); })
            .catch((err) => { if (!cancelled) { setFile(null); setError(err.message); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [routerId, generation, sha, path]);

    if (!files) return error ? <ErrorBanner msg={error} /> : <Loading label="Loading files" />;

    const shown = filter ? files.filter((f) => f.path.toLowerCase().includes(filter.toLowerCase())) : files;
    const entry = files.find((f) => f.path === path);

    return (
        <div className="bk-files">
            <div className="bk-file-list">
                <input
                    type="search"
                    className="bk-input"
                    placeholder="Filter files"
                    aria-label="Filter files"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <ul>
                    {shown.map((f) => (
                        <li key={f.path}>
                            <button
                                type="button"
                                className={`bk-file-item${f.path === path ? ' active' : ''}`}
                                onClick={() => setPath(f.path)}
                                title={f.path}
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                    {f.symlink ? 'link' : 'description'}
                                </span>
                                <span className="bk-mono bk-file-name">{f.path}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="bk-file-view">
                <div className="bk-file-view-head">
                    <span className="bk-mono">{path}</span>
                    <span className="bk-file-view-actions">
                        {entry && (
                            <span className="bk-card-meta">
                                {entry.symlink ? 'symlink' : `${entry.size} B`} · mode {entry.mode}
                            </span>
                        )}
                        {!loading && file && !file.binary && <CopyButton text={file.content} onError={setError} />}
                    </span>
                </div>
                <ErrorBanner msg={error} />
                {loading ? (
                    <Loading label="Loading file" />
                ) : file && (
                    file.binary ? (
                        <Empty icon="data_object" title="Binary file">Download the archive to get it.</Empty>
                    ) : (
                        <>
                            {file.truncated && (
                                <div className="bk-notice bk-notice-warn">Only the first 2 MiB are shown.</div>
                            )}
                            <pre className="bk-code bk-file-body">
                                {file.content.split('\n').map((line, i, arr) => (
                                    i === arr.length - 1 && line === '' ? null : (
                                        <span key={i} className="bk-code-line">
                                            <span className="bk-ln" aria-hidden="true">{i + 1}</span>
                                            {line || ' '}{'\n'}
                                        </span>
                                    )
                                ))}
                            </pre>
                        </>
                    )
                )}
            </div>
        </div>
    );
}

function CopyButton({ text, onError }) {
    const [copied, setCopied] = useState(false);

    // A different file starts as "Copy" again.
    useEffect(() => { setCopied(false); }, [text]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            onError('Could not copy to the clipboard');
        }
    };
    return (
        <button type="button" className="bk-btn" onClick={copy} title="Copy the whole file to the clipboard">
            <span className="material-symbols-outlined" aria-hidden="true">{copied ? 'check' : 'content_copy'}</span>
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
}

// ── Details ──────────────────────────────────────────────────────────
function DetailsTab({ commit, state, generation }) {
    const m = commit.manifest || {};
    const rows = [
        ['Commit', commit.sha],
        ['Parent', commit.parent || '— (first backup)'],
        ['Comment', commit.body],
        ['Written on the router', formatTime(commit.authored_at)],
        ['netcd commit', m.commit || '— (save)'],
        ['Hostname', m.hostname],
        ['Router id', m.router_id],
        ['netcd', m.netcd],
        ['Running', m.running],
        ['Image', m.image],
        ['Schema', m.schema],
        ['Config version', m.config_version],
    ];
    const serverRows = [
        ['Repository', generation ? `generation ${generation.generation}` : '—'],
        ['Server head', state?.head ? shortSha(state.head) : '—'],
        ['Last received', formatTime(state?.last_received_at)],
        ['Router sent head', state?.last_sent_head ? shortSha(state.last_sent_head) : '—'],
        ['Last acknowledgement', state?.last_ack_at
            ? `${formatTime(state.last_ack_at)} · HTTP ${state.last_ack_status} · ${shortSha(state.last_ack_head) || '""'}`
            : '—'],
    ];
    return (
        <div className="bk-details">
            <h3 className="bk-section-title">This backup</h3>
            <dl className="bk-dl">
                {rows.filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => (
                    <div key={k} className="bk-dl-row">
                        <dt>{k}</dt>
                        <dd className={/Commit|Parent|Image|netcd/.test(k) ? 'bk-mono' : ''}>{String(v)}</dd>
                    </div>
                ))}
            </dl>
            <h3 className="bk-section-title">Delivery</h3>
            <dl className="bk-dl">
                {serverRows.map(([k, v]) => (
                    <div key={k} className="bk-dl-row">
                        <dt>{k}</dt>
                        <dd className="bk-mono">{v}</dd>
                    </div>
                ))}
            </dl>
            <p className="bk-footnote">
                To restore: install the image above on the new box, then <span className="bk-mono">netc import &lt;file&gt;.tar.gz</span>,{' '}
                <span className="bk-mono">compare</span>, <span className="bk-mono">commit</span>.
            </p>
        </div>
    );
}

// ── Access log (admin) ───────────────────────────────────────────────
const ACTION_LABELS = {
    'view-file': 'Viewed file',
    'view-diff': 'Viewed changes',
    'download-archive': 'Downloaded archive',
    'new-generation': 'Started new repository',
};

function AccessTab({ routerId }) {
    const [entries, setEntries] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.fetchAccessLog(routerId)
            .then((d) => setEntries(d.entries || []))
            .catch((err) => setError(err.message));
    }, [routerId]);

    if (error) return <ErrorBanner msg={error} />;
    if (!entries) return <Loading label="Loading access log" />;
    if (entries.length === 0) return <Empty icon="policy" title="No access recorded" />;
    return (
        <div className="bk-table-wrap">
            <table className="bk-table">
                <thead>
                    <tr>
                        <th className="bk-th">When</th>
                        <th className="bk-th">Who</th>
                        <th className="bk-th">Action</th>
                        <th className="bk-th">Commit</th>
                        <th className="bk-th">Path</th>
                        <th className="bk-th">From</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map((e) => (
                        <tr key={e.id} className="bk-row-static">
                            <td className="bk-td">{formatTime(e.at)}</td>
                            <td className="bk-td">{e.principal}</td>
                            <td className="bk-td">{ACTION_LABELS[e.action] || e.action}</td>
                            <td className="bk-td bk-mono">{e.commit ? e.commit.split('..').map(shortSha).join('..') : '—'}</td>
                            <td className="bk-td bk-mono">{e.path || '—'}</td>
                            <td className="bk-td bk-mono">{e.remote_addr || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Confirm dialog ───────────────────────────────────────────────────
function ConfirmNewRepository({ routerId, busy, onCancel, onConfirm }) {
    const ref = useFocusTrap(true, busy ? undefined : onCancel);
    return (
        <div className="bk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
            <div
                ref={ref}
                className="bk-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bk-new-repo-title"
                tabIndex={-1}
            >
                <h2 id="bk-new-repo-title" className="bk-dialog-title">Start a new repository for {routerId}?</h2>
                <p className="bk-dialog-text">
                    The current repository is archived and stays readable. The router is asked to send its whole
                    history again, which then starts the new repository.
                </p>
                <p className="bk-dialog-text">
                    Only do this when the box was replaced or restored. A box that simply resent its own history
                    does not need it.
                </p>
                <div className="bk-dialog-actions">
                    <button type="button" className="bk-btn" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button type="button" className="bk-btn bk-btn-danger" onClick={onConfirm} disabled={busy}>
                        {busy ? 'Starting…' : 'Start new repository'}
                    </button>
                </div>
            </div>
        </div>
    );
}
